import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { Mutex } from "async-mutex";
import { BehaviorSubject, of, scheduled, Subscription, throwError } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { URLHelper } from "src/model/class/url-helper";
import { ApiErrorType } from "src/model/enum/enumApiErrorType";
import { GenericOperationKind } from "src/model/enum/enumGenericOperationKind";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import { EventService } from "../../../service/event.service";
import { LocalStorageService } from "../../../service/local-storage.service";
import { LoggerService } from "../../../service/logger.service";
import { MqttClientService } from "../../../service/mqtt-client.service";
import { SmartComponentNewPropertySave } from "src/components/shared-components/class/smart-components/smart-component-new-property-save";
import { SmartComponentPropertySave } from "src/components/shared-components/class/smart-components/smart-componment-property-save";
import { SmartComponentProperty } from "src/components/shared-components/class/smart-components/smart-component-property";
import { SmartComponentCapability } from "src/components/shared-components/class/smart-components/smart-component-capability";
import { SmartComponentKind } from "src/components/shared-components/class/smart-components/smart-component-kind";
import { SmartComponentTemplate } from "src/components/shared-components/class/smart-components/smart-component-template";
import { Skill } from "src/components/shared-components/class/smart-components/smart-component-skill";
import { SmartComponentEnvironmentAssociated } from "src/components/shared-components/class/smart-components/smart-component-environment-associated";
import { SmartComponent } from "src/components/shared-components/class/smart-components/smart-component";
import { GenericCounterGraphData } from "src/components/shared-components/class/generic-counter-graph-data";
import { CounterConsumptionMonthlyComparison } from "src/components/shared-components/class/counter-consumption-monthly-comparison";
import { GenericCounterMeasurementData } from "src/components/shared-components/class/generic-counter-measurements-data";
import { ViewMode } from "src/components/shared-components/enum/enumViewMode";
import { CounterDistributionConsumption } from "../class/counter-distribution-consumption";
import { AssociationSmartComponent } from "../interfaces/association-smart-component";
import { SmartComponentGraphicPage } from "../interfaces/smart-component-graphic-page";

@Injectable({
  providedIn: "root",
})
export class SmartComponentService implements OnDestroy, iEvent, iMqttHandling {
  private readonly _store = inject(Store);
  private readonly _http = inject(HttpClient);
  private readonly _eventService = inject(EventService);
  private readonly _loggerService = inject(LoggerService);
  private readonly _mqttService = inject(MqttClientService);
  private readonly _storageService = inject(LocalStorageService);

  private _logContext: string = "Smart-component.service";

  /*private _smartComponentStore: GenericApiData = null;
  private _smartComponentSubject: BehaviorSubject<Array<SmartComponent>> =
    new BehaviorSubject<Array<SmartComponent>>(null);*/

  private _isErrorGetSmartComponentListAPIRequest: boolean = false;

  private _mutex = new Mutex();
  // Gateway data format of each device kind, loaded once for the plant server it comes from
  private _gatewayDataFormats: Promise<Map<number, string>> = null;
  private _gatewayDataFormatsServer: string = null;

  // Values of fields in search address skill component

  public searchAdressesFieldsValue: BehaviorSubject<{
    name: string;
    address: string;
  }> = null;

  public _eventSubscriptions: Subscription[] = null;
  public _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;

  public get isErrorGetSmartComponentListAPIRequest(): boolean {
    return this._isErrorGetSmartComponentListAPIRequest;
  }

  /*public readonly smartComponentList =
    this._smartComponentSubject.asObservable();*/

  constructor() {
    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();
    this.searchAdressesFieldsValue = new BehaviorSubject({
      name: "",
      address: "",
    });

    this.subscribeEvents();
    this.subscribeMQTTAddresses();
  }

  public ngOnDestroy(): void {
    //this._smartComponentStore = null;

    //this._smartComponentSubject.next(null);
    //this._smartComponentSubject.complete();

    this.unsubscribeEvents();

    this.unsubscribeToMQTTAddresses();
  }

  //#region Subscribe to event-service observable
  public subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    /** Operation perfomed on logout */
    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        this._storageService.remove(AppSettings.KEY_SMART_COMPONENT);

        await this.unsubscribeToMQTTAddresses();

        //this._smartComponentStore = null;
        //this._smartComponentSubject.next(null);
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(() => {
        this.subscribeMQTTAddresses();
      })
    );
  }

  public unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Get list of smart component by id environment
   *
   * @param idEnvironment id environment to get smart component
   * @returns
   */
  public async getSmartComponentByIDEnvironment(
    idEnvironment: number,
    idUser?: number
  ): Promise<Array<SmartComponent>> {
    const mapQueryString: Map<string, string> = new Map<string, string>();
    mapQueryString.set("idEnvironment", idEnvironment.toString());
    if (idUser) {
      mapQueryString.set("idUser", idUser.toString());
    }
    return this.getListSmartComponent(mapQueryString);
  }

  /**
   * Get distribution consumption for smart component
   * @param idEnvironment id of environment
   * @param idSmartComponentKind kind of smart component
   * @param skill name of skill
   * @param date date to get distribution consumption (YYYYMM)
   * @returns
   */
  public getDistributionConsumption(
    idEnvironment: number,
    idSmartComponentKind: number,
    skill: string,
    date: number
  ): Promise<CounterDistributionConsumption[]> {
    const queryString =
      "?idEnvironment=" +
      encodeURIComponent(idEnvironment) +
      "&idSmartComponentKind=" +
      encodeURIComponent(idSmartComponentKind) +
      "&skill=" +
      encodeURIComponent(skill) +
      "&date=" +
      encodeURIComponent(date);
    return this._http
      .get<CounterDistributionConsumption[]>(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/distribution/consumption" +
        queryString
      )
      .toPromise();
  }

  /**
   * Save changes on smart component properties
   * @param smartComponentProperty
   * @returns
   */
  public async saveChangesSkills(
    smartComponentProperty: SmartComponentPropertySave
  ) {
    return this._http
      .put(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/properties",
        smartComponentProperty
      )
      .toPromise();
  }

  /**
   * Save new smart component
   * @param smartComponent
   * @returns
   */
  public async saveNewSmartComponents(
    smartComponent: SmartComponentNewPropertySave
  ): Promise<SmartComponent> {
    return this._http
      .post<SmartComponent>(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/properties",
        smartComponent
      )
      .toPromise();
  }

  /**
   * Get peaks data passing address and instanceGUID
   * @param address
   * @param instanceGUID
   * @returns
   */
  public getPeaksData(
    address: string,
    instanceGUID: string,
    startDate: string,
    endDate: string
  ): Promise<GenericCounterGraphData> {
    return this._http
      .get<GenericCounterGraphData>(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/peaks?address=" +
        encodeURIComponent(address) +
        "&instanceGuid=" +
        encodeURIComponent(instanceGUID) +
        "&startDate=" +
        encodeURIComponent(startDate) +
        "&endDate=" +
        encodeURIComponent(endDate)
      )
      .toPromise();
  }

  /**
   * Get peaks data csv passing address and instanceGUID
   * @param address
   * @param instanceGUID
   * @returns
   */
  public getPeaksDataCSV(
    address: string,
    instanceGUID: string,
    startDate: string,
    endDate: string
  ): Promise<string> {
    return this._http
      .get<string>(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/export/peaks?address=" +
        encodeURIComponent(address) +
        "&instanceGuid=" +
        encodeURIComponent(instanceGUID) +
        "&startDate=" +
        encodeURIComponent(startDate) +
        "&endDate=" +
        encodeURIComponent(endDate),
        {
          responseType: "text" as "json",
        }
      )
      .toPromise();
  }

  /**
   * API call to update value of viewMode
   * @param idSmartComponent
   * @param idEnvironment
   * @param viewMode
   * @returns
   */
  public updateViewMode(
    idSmartComponent: number,
    idEnvironment: number,
    viewMode: ViewMode
  ): Promise<SmartComponentEnvironmentAssociated> {
    return this._http
      .patch<SmartComponentEnvironmentAssociated>(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/" +
        encodeURIComponent(idSmartComponent) +
        "/" +
        encodeURIComponent(idEnvironment) +
        "/view/mode",
        {
          viewMode,
        }
      )
      .toPromise();
  }

  /**
   * Delete smart component
   * @param idSmartComponent
   * @returns
   */
  public async deleteSmartComponent(idSmartComponent) {
    return this._http
      .delete(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/" +
        idSmartComponent
      )
      .toPromise();
  }

  //#region MQTT-addresses handling
  public async subscribeMQTTAddresses(): Promise<void> {
    /*let mqttAddress: string = "";
    let mqttAddressSubcription: Subscription = null;
    let returnedObservable: Observable<IMqttMessage> = null;

    //#region Subscribing to RESERVED_DB_SMART_COMPONENT_CHANGED
    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_SMART_COMPONENT_CHANGED;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);

    mqttAddressSubcription = returnedObservable
      .pipe(
        filter(() => this._smartComponentStore !== null),
        map((mqttMessage: IMqttMessage) => {
          return this._mqttService.parseMessage(mqttMessage);
        }),
        map(async (message: Array<string>) => {
          
          if (message.length === 3) {
            const release = await this._mutex.acquire();

            try {
              const operationKind = parseInt(message[0]);
              const idSmartComponent = parseInt(message[1]);
              const clientID = message[2];
              
              if (operationKind === GenericOperationKind.INSERT) {
                const index = this._smartComponentStore.data.findIndex(
                  (sm: SmartComponent) => sm.id === idSmartComponent
                );
                if (index < 0) {
                  const inserted: SmartComponent[] =
                    await this.getSmartComponentByQuery(
                      "id=" + idSmartComponent
                    ).catch(() => {
                      return null;
                    });

                  if (inserted.length > 0) {
                    this._smartComponentStore.data.push(inserted[0]);
                    this._smartComponentStore.data =
                      this._smartComponentStore.data.filter(
                        (sm: SmartComponent) => sm.id !== null
                      );
                  } else {
                    throw new Error(
                      "OnInsertSmartComponent - Error retrieving smart component with id = " +
                        idSmartComponent
                    );
                  }
                }
              } else if (operationKind === GenericOperationKind.UPDATE) {
                const index = this._smartComponentStore.data.findIndex(
                  (sm: SmartComponent) => sm.id === idSmartComponent
                );

                if (index >= 0) {
                  const updated: SmartComponent[] =
                    await this.getSmartComponentByQuery(
                      "id=" + idSmartComponent
                    ).catch(() => {
                      return null;
                    });
                  if (updated.length) {
                    this._smartComponentStore.data[index] = updated[0];
                    this._smartComponentStore.data =
                      this._smartComponentStore.data.filter(
                        (sm: SmartComponent) => sm.id !== null
                      );
                  } else {
                    throw new Error(
                      "OnUpdateSmartComponent - Error retrieving smart component with id = " +
                        idSmartComponent
                    );
                  }
                } else {
                  throw new Error(
                    "OnUpdateSmartComponent smart component not found with id = " +
                      idSmartComponent
                  );
                }
              } else if (operationKind === GenericOperationKind.DELETE) {
                this._smartComponentStore.data =
                  this._smartComponentStore.data.filter(
                    (sm: SmartComponent) => sm.id !== idSmartComponent
                  );
              }
              return idSmartComponent + "|" + clientID;
            } finally {
              release();
            }
          } else {
            throw new Error(
              "OnSmartComponentDataChange - Incorrect message format"
            );
          }
        }),
        debounceTime(1000)
      )
      .subscribe(
        (promiseIDChanged) => {
          promiseIDChanged.then((IDChanged_ClientID) => {
            if (IDChanged_ClientID) {
              const values = IDChanged_ClientID.split("|");
              this._smartComponentStore.lastAPIModified = moment().format(
                DateTimeFormats.MOMENT_DATETIME_FORMAT
              );
              this._smartComponentSubject.next(this._smartComponentStore.data);
              this._eventService.OnSmartComponentChange.next(
                IDChanged_ClientID
              );
              this.setToStorage(this._smartComponentStore);
            }
          });
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "mqttService.subscribeAddress",
            "SUBSCRIBE ADDRESS",
            "Incorrect message format " + error.message
          );
        }
      );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );
    */
  }

  public async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY present " + addressToCheck
      );
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck
      );
      result = true;
    }
    return result;
  }

  public async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }
  //#endregion

  /**
   * Get summary consumption comparison for smart component
   * @param idEnvironment
   * @param idSmartComponentKind
   * @param skill
   * @returns
   */
  public async getSummaryYearlyForMonthConsumption(
    idEnvironment: number,
    idSmartComponentKind: number,
    skill: string
  ) {
    const queryString =
      "?idEnvironment=" +
      encodeURIComponent(idEnvironment) +
      "&idSmartComponentKind=" +
      encodeURIComponent(idSmartComponentKind) +
      "&skill=" +
      encodeURIComponent(skill);
    return this._http
      .get<GenericCounterGraphData>(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/summary/consumptionchart/" +
        queryString
      )
      .toPromise();
  }

  /**
   * Get consumption comparison for smart component
   */
  public async getYearlyForMonthConsumption(
    address: string,
    instanceGuid: string
  ): Promise<GenericCounterGraphData> {
    const queryString =
      "?address=" +
      encodeURIComponent(address) +
      "&instanceGuid=" +
      encodeURIComponent(instanceGuid);

    return this._http
      .get<GenericCounterGraphData>(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/consumptionchart/" +
        queryString
      )
      .toPromise();
  }

  /**
   * Get summary consumption comparison for smart component in CSV format
   */
  public async getSummaryYearlyForMonthConsumptionCSV(
    idEnvironment: number,
    idSmartComponentKind: number,
    skill: string
  ): Promise<string> {
    const queryString =
      "?idEnvironment=" +
      encodeURIComponent(idEnvironment) +
      "&idSmartComponentKind=" +
      encodeURIComponent(idSmartComponentKind) +
      "&skill=" +
      encodeURIComponent(skill);

    return this._http
      .get<string>(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/export/summary/consumptionchart/" +
        queryString,
        {
          responseType: "text" as "json",
        }
      )
      .toPromise();
  }

  /**
   * Get consumption comparison for smart component in CSV format
   */
  public async getYearlyForMonthConsumptionCSV(
    address: string,
    instanceGuid: string
  ): Promise<string> {
    const queryString =
      "?address=" +
      encodeURIComponent(address) +
      "&instanceGuid=" +
      encodeURIComponent(instanceGuid);

    return this._http
      .get<string>(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/export/consumptionchart/" +
        queryString,
        {
          responseType: "text" as "json",
        }
      )
      .toPromise();
  }

  /**
   * This method retrieves from server all the values which have to be displayed in the graphic renderization
   * of a specific counter-type summary SmartComponent. The kind (EX. a static , a fixed vlaue...) and the nature of the values are dependent
   * on the peculiar counter sub-type (IE. GAS, ELECTRIC, HEATING etc.)
   * @param idEnvironment
   * @param idSmartComponentKind
   * @returns
   */
  public getSmartComponentSummaryCounterMeasures(
    idEnvironment: number,
    idSmartComponentKind: number
  ): Promise<Array<GenericCounterMeasurementData>> {
    const queryString =
      "?idEnvironment=" +
      encodeURIComponent(idEnvironment) +
      "&idSmartComponentKind=" +
      encodeURIComponent(idSmartComponentKind);
    return this._http
      .get<any>(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/summary/measurement" +
        queryString
      )
      .pipe(
        switchMap((returnDataList: any) => {
          if (returnDataList !== null) {
            const counterMeasurementList: GenericCounterMeasurementData[] =
              returnDataList;
            return of(counterMeasurementList);
          } else {
            return throwError(new Error());
          }
        })
      )
      .toPromise();
  }

  /**
   * This method retrieves from server all the values which have to be displayed in the graphic renderization
   * of a specific counter-type SmartComponent. The kind (EX. a static , a fixed vlaue...) and the nature of the values are dependent
   * on the peculiar counter sub-type (IE. GAS, ELECTRIC, HEATING etc.)
   * @param idSmartComponent
   * @param idEnvironment
   * @returns
   */
  public async getSmartComponentCounterMeasures(
    idSmartComponent: number,
    idEnvironment: number
  ): Promise<Array<GenericCounterMeasurementData>> {
    const queryString =
      "?idEnvironment=" +
      encodeURIComponent(idEnvironment) +
      "&idSmartComponent=" +
      encodeURIComponent(idSmartComponent);
    return this._http
      .get<any>(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/measurement" +
        queryString
      )
      .pipe(
        switchMap((returnDataList: any) => {
          if (returnDataList !== null) {
            const counterMeasurementList: GenericCounterMeasurementData[] =
              returnDataList;
            return of(counterMeasurementList);
          } else {
            return throwError(new Error());
          }
        })
      )
      .toPromise();
  }
  //#endregion

  /**
   * Get smart component by id
   */
  public getSmartComponentKind(): Promise<Array<SmartComponentKind>> {
    return this._http
      .get<Array<SmartComponentKind>>(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/kind"
      )
      .toPromise();
  }

  /**
   * Get environments by id smart component
   * @param idSmartComponent
   * @returns
   */
  public getEnvironmentByIdSmartComponent(
    idSmartComponent: number
  ): Promise<Array<SmartComponentEnvironmentAssociated>> {
    return this._http
      .get<Array<SmartComponentEnvironmentAssociated>>(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/environmentbysmartcomponent/" +
        idSmartComponent
      )
      .toPromise();
  }

  /**
   * Associate smart component to environment
   * @param idSmartComponent
   * @param environment
   * @returns
   */
  public addAssociateSmartComponentEnvironment(
    smartComponentsToAssociate: AssociationSmartComponent[]
  ): Promise<AssociationSmartComponent[]> {
    return this._http
      .post<AssociationSmartComponent[]>(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/environment",
        {
          association: smartComponentsToAssociate,
        }
      )
      .toPromise();
  }

  /**
   *  Delete associate smart component to environment
   * @param idSmartComponent
   * @param environment
   * @returns
   */
  public deleteSmartComponentEnvironment(idAssociation) {
    return this._http
      .delete(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/environment/" +
        idAssociation
      )
      .toPromise();
  }

  /**
   * Get template for create new smart component
   */
  public getTemplateSmartComponent(kind: number): Promise<Array<Skill>> {
    return this._http
      .get<Array<Skill>>(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/skillsbykind/" +
        kind
      )
      .toPromise();
  }

  /**
   * Get smart component by idArea
   *
   * @param idArea id area to search smart component
   * @returns
   */
  public getSmartComponentByIdArea(
    idArea: number
  ): Promise<Array<SmartComponent>> {
    const mapQueryString: Map<string, string> = new Map<string, string>();
    if (idArea > 0) {
      mapQueryString.set("idArea", idArea.toString());
    }

    return this.getListSmartComponent(mapQueryString);
  }

  /**
   * Get smart component by id smartcomponent
   *
   * @param id id of smart component to search
   * @returns
   */
  public getSmartComponentById(id: number): Promise<Array<SmartComponent>> {
    const mapQueryString: Map<string, string> = new Map<string, string>();
    mapQueryString.set("id", id.toString());

    return this.getListSmartComponent(mapQueryString);
  } //

  /**
   * Insert new smartcomponent
   */
  public insertNewSmartComponent(body): Promise<Array<SmartComponentKind>> {
    return this._http
      .post<Array<SmartComponentKind>>(
        environment.httpServerRoot + AppSettings.API_URL_SMART_COMPONENT + "",
        body
      )
      .toPromise();
  }

  /**
   * Get Skill for smart component kind
   * @param kind
   * @returns
   */
  public getSmartComponentSkill(
    kind: number
  ): Promise<Array<SmartComponentTemplate>> {
    return this._http
      .get<Array<SmartComponentTemplate>>(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/skillsbykind/" +
        kind
      )
      .toPromise();
  }

  /**
   * Delete association smart component environment
   * @param body
   * @returns
   */
  public deleteAssociationSmartComponentEnvironment(id) {
    return this._http
      .delete(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/environment/" +
        id
      )
      .toPromise();
  }

  /**
   * Enable/disable multiple smart component
   * @param ids id of smart components to enable/disable
   */
  public async enableDisableMultipleSmartComponent(
    ids: number[]
  ): Promise<void> {
    return this._http
      .post(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/enable",
        ids
      )
      .pipe(
        tap(() => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_SMART_COMPONENT_CHANGED;

          let mqttMessage = "";

          for (let i = 0; i < ids.length; i++) {
            mqttMessage =
              GenericOperationKind.UPDATE.toString() + "|" + ids[i].toString();
            /** Fire propagation to all connected client */
            this.publishAddress(mqttAddress, mqttMessage);
          }

          this.sendReservedReloadSmartComponent();
        })
      )
      .toPromise()
      .then(
        () => { },
        (error) => {
          throw new Error(error);
        }
      );
  }

  /**
   * Get smart component properties for specific smart component
   *
   * @param idSmartComponent id smart component to get property
   */
  public async getSmartComponentSkillsByIDSmartComponent(
    idSmartComponent: number,
    idEnvironment: number = 0
  ): Promise<SmartComponentProperty> {
    let queryString = "?idSmartComponent=" + idSmartComponent;
    if (idEnvironment > 0) {
      queryString += "&idEnvironment=" + idEnvironment;
    }
    return this._http
      .get<SmartComponentProperty>(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        "/properties" +
        queryString
      )
      .toPromise();
  }

  /**
   * API call to save order of smart component
   * @param idUser
   * @param idEnvironment
   * @param sortingArray array with the new order of smart components
   * @returns promise for api call
   */
  public changeSmartComponentOrder(
    idUser: number,
    sortingArray: SmartComponentOrder[]
  ): Promise<SmartComponentOrder> {
    return this._http
      .put<SmartComponentOrder>(
        environment.httpServerRoot +
        AppSettings.API_URL_SMART_COMPONENT +
        `/${idUser}`,
        { sorting: sortingArray }
      )
      .toPromise();
  }

  /**
   * Get the list of all device kinds
   * @returns 
   */
  public getAllDeviceKind(): Promise<Array<Object>> {
    return this._http
      .get<Array<Object>>(environment.httpServerRoot + AppSettings.API_URL_DEVICE + `/kind`).toPromise();
  }

  /**
   * Get the gateway data format of each device kind (by id). The list is loaded once and
   * kept for the plant server it comes from: a failed load is not kept, so it is retried
   * @returns map from device kind id to gateway data format
   */
  public getGatewayDataFormats(): Promise<Map<number, string>> {
    if (
      !this._gatewayDataFormats ||
      this._gatewayDataFormatsServer !== environment.httpServerRoot
    ) {
      this._gatewayDataFormatsServer = environment.httpServerRoot;
      this._gatewayDataFormats = this.getAllDeviceKind()
        .then(
          (deviceKinds: Array<any>) =>
            new Map<number, string>(
              (deviceKinds ?? []).map((deviceKind: any) => [
                deviceKind.id,
                deviceKind.gatewayDataFormat,
              ]),
            ),
        )
        .catch((error) => {
          this._gatewayDataFormats = null;
          throw error;
        });
    }

    return this._gatewayDataFormats;
  }

  /**
   * Save smart components positions and text orientation for smart components in graphic page
   * @param smartComponents smart components to save in the graphic page
   * @returns Promise with the saved smart components
   */
  public saveSmartComponentsSettingsGraphicPage(smartComponents: SmartComponentGraphicPage[]): Promise<SmartComponentGraphicPage[]> {
    return this._http.put<Array<SmartComponentGraphicPage>>(environment.httpServerRoot + AppSettings.API_URL_SMART_COMPONENT + '/graphicpage', smartComponents).toPromise();
  }

  //#region mqtt
  /**
   * Used to write an address on the mqtt
   * @param mqttAddress address to write
   * @param value value of the address: ONLY STRING value
   */
  private publishAddress(mqttAddress: string, value: string): void {
    this._mqttService.publishAddress(mqttAddress, value);
  }

  /**
   * Send object reload command
   */
  private sendReservedReloadSmartComponent(): void {
    const mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_RELOAD_SMART_COMPONENT;

    this._mqttService.publishAddress(mqttAddress, "1", "REFRESH");
  }
  //#endregion

  /**
   * Get array of smart component by specific where clause
   *
   * @param queryMap Query map is generic map to build query string for getting query string. It accepts: key for key query parameter, value for value query parameter
   * @returns
   */
  private async getListSmartComponent(
    queryMap: Map<string, string>
  ): Promise<Array<SmartComponent>> {
    const helper: URLHelper = new URLHelper();

    const url: URL = new URL(
      environment.httpServerRoot + AppSettings.API_URL_SMART_COMPONENT
    );
    const formattedUrl: URL = helper.buildFullURLByQueryString(url, queryMap);

    return this._http
      .get<Array<SmartComponent>>(formattedUrl.toString())
      .toPromise();
  }
}
